import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";

export type StageStatus = "pending" | "active" | "completed";

interface StageCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  status: StageStatus;
  index: number;
}

const StageCard = ({ icon: Icon, title, description, status, index }: StageCardProps) => {
  return (
    <div
      role="listitem"
      aria-current={status === "active" ? "step" : undefined}
      className={cn(
        "flex items-start gap-4 p-4 rounded-xl border transition-all duration-300",
        status === "pending" && "bg-white/60 border-gray-100 opacity-0",
        status === "active" && "bg-white border-gray-100 shadow-sm opacity-100",
        status === "completed" && "bg-white border-gray-100 shadow-sm opacity-100"
      )}
      style={{
        animation:
          status !== "pending"
            ? `slide-in-right 500ms ease ${index * 150}ms forwards`
            : undefined,
      }}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
          status === "pending" && "bg-gray-50",
          status === "active" && "bg-emerald-50",
          status === "completed" && "bg-emerald-50"
        )}
      >
        {status === "active" && (
          <Loader2
            className="w-5 h-5 text-emerald-600 animate-spin"
            aria-label="Processing"
          />
        )}
        {status === "completed" && (
          <Check
            className="w-5 h-5 text-emerald-600"
            aria-label="Completed"
            style={{ animation: "check-pop 400ms ease forwards" }}
          />
        )}
        {status === "pending" && (
          <Icon className="w-5 h-5 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "text-sm font-medium",
            status === "pending" ? "text-slate-400" : "text-slate-800"
          )}
        >
          {title}
        </div>
        <div
          className={cn(
            "text-xs mt-0.5",
            status === "pending" ? "text-slate-400" : "text-slate-500"
          )}
        >
          {description}
        </div>
      </div>
    </div>
  );
};

export default StageCard;
